import { useState, type FormEvent } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildCategoryCreate, buildCategoryUpdate } from './categoryPayload'
import { saveCategoryCreate, saveCategoryUpdate } from './saveCategory'
import type { Category } from '@/offline/types'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

/** Product-type categories: create, rename (keeps device links), archive (hides from new entry), reorder. */
export function CategoriesPage() {
  const ws = getActiveWorkspaceId()
  const categories = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).categories.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )

  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  const all = categories ?? []
  const active = all.filter((c) => !c.archived_at).sort((a, b) => a.sort_order - b.sort_order)
  const archived = all.filter((c) => c.archived_at).sort((a, b) => a.sort_order - b.sort_order)

  async function run(fn: () => Promise<unknown>) {
    setError(null)
    if (!ws) return
    setBusy(true)
    try {
      const summary = (await fn()) as { rejected: { result: { status: string } }[] }
      if (summary.rejected.length > 0) setError(`Not saved: ${summary.rejected[0].result.status}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  function onAdd(e: FormEvent) {
    e.preventDefault()
    if (!ws) return
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    const nextSort = active.length > 0 ? Math.max(...active.map((c) => c.sort_order)) + 10 : 10
    return run(async () => {
      const s = await saveCategoryCreate(ws, buildCategoryCreate(ws, trimmed, brand.trim() || null, nextSort))
      if (s.rejected.length === 0) {
        setName('')
        setBrand('')
      }
      return s
    })
  }

  function startEdit(c: Category) {
    setEditing(c.id)
    setEditName(c.name)
  }

  function saveEdit(c: Category) {
    const trimmed = editName.trim()
    if (!trimmed || trimmed === c.name) {
      setEditing(null)
      return
    }
    return run(async () => {
      const s = await saveCategoryUpdate(ws!, buildCategoryUpdate(c, { name: trimmed }))
      if (s.rejected.length === 0) setEditing(null)
      return s
    })
  }

  function toggleArchive(c: Category) {
    return run(() => saveCategoryUpdate(ws!, buildCategoryUpdate(c, { archived: !c.archived_at })))
  }

  function move(c: Category, dir: -1 | 1) {
    const idx = active.findIndex((x) => x.id === c.id)
    const swapWith = active[idx + dir]
    if (!swapWith) return
    return run(async () => {
      const a = await saveCategoryUpdate(ws!, buildCategoryUpdate(c, { sort_order: swapWith.sort_order }))
      if (a.rejected.length > 0) return a
      return saveCategoryUpdate(ws!, buildCategoryUpdate(swapWith, { sort_order: c.sort_order }))
    })
  }

  return (
    <Page title="Categories" note="Product types. Rename keeps history; archive hides from new entry.">
      <form onSubmit={onAdd} className="mb-4 space-y-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls} htmlFor="cat-name">Name *</label>
            <input id="cat-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tablet" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cat-brand">Brand code</label>
            <input id="cat-brand" className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="optional (apple…)" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="w-full rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? '…' : 'Add category'}
        </button>
        {error ? <p role="alert" className="text-xs text-rose-300">{error}</p> : null}
      </form>

      {active.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
          No active categories. Add one above, or sync to pull your starter set.
        </p>
      ) : (
        <ul className="space-y-2">
          {active.map((c, i) => (
            <li key={c.id} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button type="button" aria-label="Move up" disabled={busy || i === 0} onClick={() => move(c, -1)}
                    className="text-slate-400 disabled:opacity-25">▲</button>
                  <button type="button" aria-label="Move down" disabled={busy || i === active.length - 1} onClick={() => move(c, 1)}
                    className="text-slate-400 disabled:opacity-25">▼</button>
                </div>
                {editing === c.id ? (
                  <input className={inputCls} value={editName} autoFocus
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => saveEdit(c)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit(c)
                      if (e.key === 'Escape') setEditing(null)
                    }} />
                ) : (
                  <div className="flex-1">
                    <p className="font-medium text-slate-100">{c.name}</p>
                    {c.brand_code ? <p className="text-[11px] text-slate-500">{c.brand_code}</p> : null}
                  </div>
                )}
                {editing === c.id ? (
                  <button type="button" onClick={() => saveEdit(c)} disabled={busy}
                    className="rounded-md bg-sky-500 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Save</button>
                ) : (
                  <button type="button" onClick={() => startEdit(c)} disabled={busy}
                    className="rounded-md border border-slate-700 px-3 py-1 text-xs font-semibold text-slate-300 disabled:opacity-60">Rename</button>
                )}
                <button type="button" onClick={() => toggleArchive(c)} disabled={busy}
                  className="rounded-md border border-slate-700 px-3 py-1 text-xs font-semibold text-slate-300 disabled:opacity-60">Archive</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 ? (
        <div className="mt-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Archived</h2>
          <ul className="space-y-2">
            {archived.map((c) => (
              <li key={c.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/20 px-3 py-2.5">
                <div>
                  <p className="font-medium text-slate-400 line-through">{c.name}</p>
                  {c.brand_code ? <p className="text-[11px] text-slate-600">{c.brand_code}</p> : null}
                </div>
                <button type="button" onClick={() => toggleArchive(c)} disabled={busy}
                  className="rounded-md border border-slate-700 px-3 py-1 text-xs font-semibold text-emerald-400 disabled:opacity-60">
                  Unarchive
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Page>
  )
}
